import { useSyncExternalStore } from "react";
import type { AlertKind } from "./alerts";
import { HEARTBEAT_URL } from "./clips";

// Alert sounds for HQ (ADR-0015). Every kind has its own WebAudio tone, synthesised in the
// browser (no file to download, so it plays even when a clip cannot be loaded), so HQ can tell
// them apart without looking, followed by the recorded Polish announcement (clips.ts, files
// fetched once and kept decoded). SOS is the loudest and loops until acknowledged (docs/03 "SOS").
//
// Sound is part of the system and cannot be switched off (owner, 2026-10-10). Browsers keep audio
// locked after every page load until the person interacts with the page (autoplay policy), and
// no page can lift that itself. So it unlocks on the first click or key anywhere in the panel;
// Chrome's AutoplayAllowlist policy on HQ laptops removes even that click (docs/runbook.md).

let context: AudioContext | null = null;
let sosTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function changed() {
  for (const l of listeners) l();
}

function unlock() {
  void context?.resume();
  // Asking from a user gesture is what browsers expect; once granted it is remembered for good.
  if (typeof Notification !== "undefined" && Notification.permission === "default") {
    void Notification.requestPermission();
  }
}

/**
 * Creates the audio context (already running if the browser allows this site to play) and
 * unlocks it on the first interaction. Call once when the panel starts; returns a cleanup.
 */
export function installSoundUnlock(): () => void {
  context ??= new AudioContext();
  context.onstatechange = changed;
  // Ready before the first chime; a suspended context decodes too.
  if (HEARTBEAT_URL) loadClip(context, HEARTBEAT_URL).catch(logHeartbeatError);
  const events = ["pointerdown", "keydown"] as const;
  for (const e of events) window.addEventListener(e, unlock, { capture: true });
  return () => {
    for (const e of events) window.removeEventListener(e, unlock, { capture: true });
  };
}

export function isSoundArmed(): boolean {
  return context?.state === "running";
}

export function useSoundArmed(): boolean {
  return useSyncExternalStore((l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  }, isSoundArmed);
}

type Tone = { hz: number; at: number; length: number; wave?: OscillatorType; volume?: number };

const CUES: Record<Exclude<AlertKind, "sos">, Tone[]> = {
  // Friendly two-note "ding-ding".
  message: [
    { hz: 660, at: 0, length: 0.12 },
    { hz: 880, at: 0.15, length: 0.18 },
  ],
  // Rising triad: something was found.
  finding: [
    { hz: 523, at: 0, length: 0.12, wave: "triangle" },
    { hz: 659, at: 0.14, length: 0.12, wave: "triangle" },
    { hz: 784, at: 0.28, length: 0.25, wave: "triangle" },
  ],
  recall: [{ hz: 784, at: 0, length: 0.4 }],
  joined: [{ hz: 523, at: 0, length: 0.15 }],
  // Falling square tones: a person may be in trouble.
  tracking_stopped: [
    { hz: 440, at: 0, length: 0.2, wave: "square", volume: 0.2 },
    { hz: 330, at: 0.25, length: 0.3, wave: "square", volume: 0.2 },
  ],
  no_position: [
    { hz: 392, at: 0, length: 0.2, wave: "square", volume: 0.2 },
    { hz: 294, at: 0.25, length: 0.3, wave: "square", volume: 0.2 },
  ],
  connection_lost: [
    { hz: 220, at: 0, length: 0.15, wave: "sawtooth", volume: 0.2 },
    { hz: 220, at: 0.25, length: 0.15, wave: "sawtooth", volume: 0.2 },
    { hz: 196, at: 0.5, length: 0.3, wave: "sawtooth", volume: 0.2 },
  ],
  connection_back: [
    { hz: 523, at: 0, length: 0.12 },
    { hz: 784, at: 0.15, length: 0.2 },
  ],
};

function play(tones: Tone[]) {
  // A suspended context (sound locked until the first click) keeps its clock still, so tones
  // queued now would all sound at once on unlock: the minute blips of an hour in one bang.
  if (context?.state !== "running") return;
  for (const tone of tones) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = tone.wave ?? "sine";
    oscillator.frequency.value = tone.hz;
    gain.gain.value = tone.volume ?? 0.3;
    oscillator.connect(gain).connect(context.destination);
    const start = context.currentTime + tone.at;
    oscillator.start(start);
    oscillator.stop(start + tone.length);
  }
}

export function playCue(kind: Exclude<AlertKind, "sos">): void {
  play(CUES[kind]);
}

const loading = new Map<string, Promise<AudioBuffer>>();
const loaded = new Map<string, AudioBuffer>();
const CLIP_TIMEOUT_MS = 15_000;

/**
 * Fetches and decodes a clip once. A failure (flaky LTE at HQ) is forgotten, so the next play
 * tries again instead of staying silent until the page is reloaded.
 */
function loadClip(ctx: AudioContext, url: string): Promise<AudioBuffer> {
  let buffer = loading.get(url);
  if (!buffer) {
    buffer = fetch(url, { signal: AbortSignal.timeout(CLIP_TIMEOUT_MS) })
      .then((res) => {
        if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((data) => ctx.decodeAudioData(data))
      .then(
        (decoded) => {
          loaded.set(url, decoded);
          return decoded;
        },
        (error: unknown) => {
          loading.delete(url);
          throw error;
        },
      );
    loading.set(url, buffer);
  }
  return buffer;
}

/** Plays a recorded clip `delayS` seconds from now (after the alert's tone), at `volume`. */
export async function playClip(url: string, delayS: number, volume = 1): Promise<void> {
  if (context?.state !== "running") return;
  const ctx = context;
  playBuffer(ctx, await loadClip(ctx, url), delayS, volume);
}

function playBuffer(ctx: AudioContext, buffer: AudioBuffer, delayS: number, volume: number) {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.value = volume;
  source.connect(gain).connect(ctx.destination);
  source.start(ctx.currentTime + delayS);
}

const SOS_RING: Tone[] = [
  { hz: 880, at: 0, length: 0.25, wave: "square", volume: 0.25 },
  { hz: 880, at: 0.4, length: 0.25, wave: "square", volume: 0.25 },
];

// "Ratun works" (issues #87, owner 2026-10-10): the same short rising chime as the phone's
// (packages/sounds/clips/heartbeat.mp3), so HQ and responders know one sound. The first blip was
// an 80 ms beep nobody noticed. A bit quieter than alerts: it plays every minute.
const HEARTBEAT_VOLUME = 0.5;
// While the clip is not decoded yet (or cannot be fetched) the same arpeggio as a tone, so HQ
// never gets silence where it expects "Ratun works" (ADR-0015).
const HEARTBEAT_TONE: Tone[] = [1046.5, 1318.5, 1568, 2093].map((hz, i) => ({
  hz,
  at: i * 0.13,
  length: 0.35,
  volume: 0.15,
}));

export function playHeartbeat(): void {
  if (context?.state !== "running") return;
  const clip = HEARTBEAT_URL && loaded.get(HEARTBEAT_URL);
  if (clip) {
    playBuffer(context, clip, 0, HEARTBEAT_VOLUME);
    return;
  }
  play(HEARTBEAT_TONE);
  if (HEARTBEAT_URL) loadClip(context, HEARTBEAT_URL).catch(logHeartbeatError);
}

function logHeartbeatError(error: unknown) {
  console.error("heartbeat clip failed, the tone plays instead", error);
}

/** Two-tone alarm repeated every 1.2 s until stopped. */
export function startSosLoop(): void {
  if (sosTimer || !context) return;
  play(SOS_RING);
  sosTimer = setInterval(() => play(SOS_RING), 1200);
}

export function stopSosLoop(): void {
  if (sosTimer) clearInterval(sosTimer);
  sosTimer = null;
}
